import { PERMISSIONS } from '@ibe/constants';
import { Button, StatusBadge } from '@ibe/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ImportDialog } from '../../../components/import-dialog/import-dialog.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { PermissionGuard } from '../../../auth/permission-guard.js';
import { currencyRowSchema } from '../api/schemas.js';

/**
 * The currencies screen.
 *
 * A currency list is read as a lookup table, and nobody scans it by creation
 * date — the default sort is by code.
 */
export function CurrencyListPage(): React.JSX.Element {
  const [importing, setImporting] = useState(false);

  return (
    <>
      <ResourceListPage
        title="Currencies"
        description="ISO 4217 currencies. Search by name or code."
        path="/v1/currencies"
        resource="currencies"
        schema={currencyRowSchema}
        permission={PERMISSIONS.REFERENCE_READ}
        rowKey={(row) => row.id}
        defaultSort={{ columnId: 'code', direction: 'asc' }}
        actions={
          <PermissionGuard permission={PERMISSIONS.REFERENCE_IMPORT}>
            <Button
              onClick={() => {
                setImporting(true);
              }}
            >
              Import
            </Button>
          </PermissionGuard>
        }
        columns={[
          {
            id: 'code',
            header: 'Code',
            sortable: true,
            fields: ['code'],
            cell: (row) => (
              <Link
                className="font-medium underline-offset-4 hover:underline"
                to={`/currencies/${row.id}`}
              >
                {row.code}
              </Link>
            ),
          },
          { id: 'name', header: 'Name', sortable: true, fields: ['name'], cell: (row) => row.name },
          { id: 'symbol', header: 'Symbol', fields: ['symbol'], cell: (row) => row.symbol ?? '—' },
          {
            id: 'minorUnit',
            header: 'Minor unit',
            fields: ['minorUnit'],
            cell: (row) => row.minorUnit ?? '—',
          },
          {
            id: 'isActive',
            header: 'Status',
            fields: ['isActive'],
            cell: (row) => (
              <StatusBadge tone={row.isActive === true ? 'success' : 'neutral'}>
                {row.isActive === true ? 'Active' : 'Inactive'}
              </StatusBadge>
            ),
          },
        ]}
      />

      <ImportDialog
        resource="currencies"
        basePath="/v1/currencies"
        open={importing}
        onClose={() => {
          setImporting(false);
        }}
      />
    </>
  );
}
